import { useState, useEffect } from "react";
import { AppLayout } from "@/components/AppLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger
} from "@/components/ui/dialog";
import {
  UserCircle, Plus, Search, Pencil, Trash2, Phone, Mail,
  Building2, MapPin, FileText, Loader2
} from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function ClientForm({ client, onSave, onClose }) {
  const [form, setForm] = useState({
    name: client?.name || "",
    client_type: client?.client_type || "particulier",
    email: client?.email || "",
    phone: client?.phone || "",
    address: client?.address || "",
    company_name: client?.company_name || "",
    notes: client?.notes || ""
  });
  const [saving, setSaving] = useState(false);

  function handleChange(field, value) {
    setForm(function(prev) { return { ...prev, [field]: value }; });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Le nom est requis"); return; }
    setSaving(true);
    try {
      const url = client?.client_id
        ? API_URL + "/api/clients/" + client.client_id
        : API_URL + "/api/clients";
      const method = client?.client_id ? "PUT" : "POST";
      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(form)
      });
      if (res.ok) {
        toast.success(client?.client_id ? "Client modifié" : "Client créé");
        onSave();
        onClose();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Erreur");
      }
    } catch (e) { toast.error("Erreur de connexion"); }
    setSaving(false);
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <Label>Nom complet / Raison sociale</Label>
          <Input value={form.name} onChange={function(e) { handleChange("name", e.target.value); }} data-testid="client-name-input" />
        </div>
        <div>
          <Label>Type</Label>
          <Select value={form.client_type} onValueChange={function(v) { handleChange("client_type", v); }}>
            <SelectTrigger data-testid="client-type-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="particulier">Particulier</SelectItem>
              <SelectItem value="entreprise">Entreprise</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Email</Label>
          <Input type="email" value={form.email} onChange={function(e) { handleChange("email", e.target.value); }} data-testid="client-email-input" />
        </div>
        <div>
          <Label>Téléphone</Label>
          <Input value={form.phone} onChange={function(e) { handleChange("phone", e.target.value); }} data-testid="client-phone-input" />
        </div>
        {form.client_type === "entreprise" && (
          <div>
            <Label>Nom de l'entreprise</Label>
            <Input value={form.company_name} onChange={function(e) { handleChange("company_name", e.target.value); }} data-testid="client-company-input" />
          </div>
        )}
        <div className="col-span-2">
          <Label>Adresse</Label>
          <Input value={form.address} onChange={function(e) { handleChange("address", e.target.value); }} data-testid="client-address-input" />
        </div>
        <div className="col-span-2">
          <Label>Notes</Label>
          <Textarea value={form.notes} onChange={function(e) { handleChange("notes", e.target.value); }} rows={3} data-testid="client-notes-input" />
        </div>
      </div>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>Annuler</Button>
        <Button type="submit" disabled={saving} data-testid="save-client-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
          {client?.client_id ? "Modifier" : "Créer"}
        </Button>
      </DialogFooter>
    </form>
  );
}

function ClientDetail({ client, onClose, onRefresh }) {
  const [contracts, setContracts] = useState([]);

  useEffect(function() {
    fetch(API_URL + "/api/clients/" + client.client_id + "/contracts", { credentials: "include" })
      .then(function(r) { return r.json(); })
      .then(setContracts)
      .catch(function() {});
  }, [client.client_id]);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <p className="text-xs text-muted-foreground">Type</p>
          <Badge variant="secondary">{client.client_type === "entreprise" ? "Entreprise" : "Particulier"}</Badge>
        </div>
        {client.email && (
          <div>
            <p className="text-xs text-muted-foreground">Email</p>
            <p className="text-sm flex items-center gap-1"><Mail className="w-3 h-3" /> {client.email}</p>
          </div>
        )}
        {client.phone && (
          <div>
            <p className="text-xs text-muted-foreground">Téléphone</p>
            <p className="text-sm flex items-center gap-1"><Phone className="w-3 h-3" /> {client.phone}</p>
          </div>
        )}
        {client.address && (
          <div>
            <p className="text-xs text-muted-foreground">Adresse</p>
            <p className="text-sm flex items-center gap-1"><MapPin className="w-3 h-3" /> {client.address}</p>
          </div>
        )}
        {client.company_name && (
          <div>
            <p className="text-xs text-muted-foreground">Entreprise</p>
            <p className="text-sm flex items-center gap-1"><Building2 className="w-3 h-3" /> {client.company_name}</p>
          </div>
        )}
      </div>
      {client.notes && (
        <div>
          <p className="text-xs text-muted-foreground mb-1">Notes</p>
          <p className="text-sm bg-slate-50 p-3 rounded-md">{client.notes}</p>
        </div>
      )}
      <div>
        <p className="text-xs text-muted-foreground mb-2">Contrats associés ({contracts.length})</p>
        {contracts.length === 0 ? (
          <p className="text-sm text-muted-foreground">Aucun contrat associé</p>
        ) : (
          <div className="space-y-2">
            {contracts.map(function(c) {
              return (
                <div key={c.contract_id} className="flex items-center gap-2 p-2 rounded border border-slate-200 text-sm">
                  <FileText className="w-4 h-4 text-blue-500" />
                  <span className="flex-1">{c.title}</span>
                  <Badge variant="outline">{c.status}</Badge>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default function Clients({ user, setUser }) {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [viewingClient, setViewingClient] = useState(null);

  useEffect(function() { fetchClients(); }, []);

  async function fetchClients() {
    try {
      const res = await fetch(API_URL + "/api/clients", { credentials: "include" });
      if (res.ok) setClients(await res.json());
    } catch (e) { /* ignore */ }
    setLoading(false);
  }

  async function handleDelete(clientId) {
    if (!window.confirm("Supprimer ce client ?")) return;
    try {
      const res = await fetch(API_URL + "/api/clients/" + clientId, { method: "DELETE", credentials: "include" });
      if (res.ok) { toast.success("Client supprimé"); fetchClients(); }
    } catch (e) { toast.error("Erreur"); }
  }

  const filtered = clients.filter(function(c) {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return c.name.toLowerCase().includes(q) || (c.email || "").toLowerCase().includes(q) || (c.company_name || "").toLowerCase().includes(q);
  });

  return (
    <AppLayout user={user} setUser={setUser}>
      <div className="p-8" data-testid="clients-page">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-2xl font-semibold tracking-tight mb-1">Clients</h1>
            <p className="text-sm text-muted-foreground">
              {clients.length} client{clients.length !== 1 ? "s" : ""} enregistré{clients.length !== 1 ? "s" : ""}
            </p>
          </div>
          <Dialog open={showForm} onOpenChange={function(open) { if (!open) { setEditingClient(null); } setShowForm(open); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-client-btn" onClick={function() { setEditingClient(null); setShowForm(true); }}>
                <Plus className="w-4 h-4 mr-2" /> Nouveau client
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>{editingClient ? "Modifier le client" : "Nouveau client"}</DialogTitle>
                <DialogDescription>Remplissez les informations du client</DialogDescription>
              </DialogHeader>
              <ClientForm client={editingClient} onSave={fetchClients} onClose={function() { setShowForm(false); setEditingClient(null); }} />
            </DialogContent>
          </Dialog>
        </div>

        <div className="flex gap-3 mb-6">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input value={searchQuery} onChange={function(e) { setSearchQuery(e.target.value); }} placeholder="Rechercher un client..." className="pl-9" data-testid="search-clients" />
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>
        ) : filtered.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center">
              <UserCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-semibold mb-1">{searchQuery ? "Aucun résultat" : "Aucun client"}</h3>
              <p className="text-sm text-muted-foreground mb-4">
                {searchQuery ? "Essayez une autre recherche" : "Créez votre premier client"}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-3">
            {filtered.map(function(c) {
              return (
                <div
                  key={c.client_id}
                  className="flex items-center gap-4 p-4 rounded-lg border border-slate-200 hover:border-blue-200 bg-white transition-colors"
                  data-testid={"client-" + c.client_id}
                >
                  <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
                    {c.client_type === "entreprise"
                      ? <Building2 className="w-5 h-5 text-blue-600" />
                      : <UserCircle className="w-5 h-5 text-blue-600" />
                    }
                  </div>
                  <div className="flex-1 min-w-0 cursor-pointer" onClick={function() { setViewingClient(c); }}>
                    <p className="text-sm font-medium">{c.name}</p>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                      {c.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{c.email}</span>}
                      {c.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{c.phone}</span>}
                      <Badge variant="secondary" className="text-xs">{c.client_type === "entreprise" ? "Entreprise" : "Particulier"}</Badge>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={function() { setEditingClient(c); setShowForm(true); }} data-testid={"edit-client-" + c.client_id}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={function() { handleDelete(c.client_id); }} data-testid={"delete-client-" + c.client_id}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Client detail dialog */}
        <Dialog open={!!viewingClient} onOpenChange={function(open) { if (!open) setViewingClient(null); }}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{viewingClient?.name}</DialogTitle>
              <DialogDescription>Fiche client détaillée</DialogDescription>
            </DialogHeader>
            {viewingClient && <ClientDetail client={viewingClient} onClose={function() { setViewingClient(null); }} onRefresh={fetchClients} />}
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  );
}
